import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '@/components/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { CheckCircle2, Clock, AlertTriangle, Calendar, ClipboardList, GitBranch, ListTodo, CheckSquare, AlertCircle, XCircle, Zap, RotateCcw, Workflow, Eye, Package } from 'lucide-react';
import { formatDate } from '@/lib/utils';
import { isAfter, isBefore, isToday, parseISO, addHours, differenceInHours, differenceInMinutes, startOfDay } from 'date-fns';
import { formatCurrency } from '@/utils/formatters';
import { useTaskCompletionBehavior } from '@/hooks/useTaskCompletion';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  in_progress: 'bg-blue-100 text-blue-800 border-blue-200',
  completed: 'bg-green-100 text-green-800 border-green-200',
  done: 'bg-green-100 text-green-800 border-green-200',
  overdue: 'bg-red-100 text-red-800 border-red-200',
  future: 'bg-indigo-100 text-indigo-800 border-indigo-200'
};

export default function MyTasksPage() {
  const { user, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [delegationTasks, setDelegationTasks] = useState([]);
  const [checklistTasks, setChecklistTasks] = useState([]);
  const [swsTasks, setSwsTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  
  // SWS completion dialog (for generic tasks without pre-defined actions)
  const [swsDialog, setSwsDialog] = useState({ open: false, task: null });
  const [completionNotes, setCompletionNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // SWS View dialog (for tasks with pre-defined actions like Create BOM, Create WO)
  const [swsViewDialog, setSwsViewDialog] = useState({ open: false, task: null });

  // CA/PA Completion dialogs
  const [caDialog, setCaDialog] = useState({ open: false, task: null, complaint: null });
  const [paDialog, setPaDialog] = useState({ open: false, task: null, complaint: null });
  const [caForm, setCaForm] = useState({
    completion_notes: '',
    cost_analysis: {
      material_losses: [],
      manpower_losses: [],
      other_costs_description: '',
      other_costs_amount: 0
    }
  });
  const [paForm, setPaForm] = useState({ 
    root_cause_analysis: '',
    preventive_action: '',
    completion_notes: '' 
  });

  const fetchAllTasks = async () => {
    setLoading(true);
    try {
      const [delegationRes, checklistRes, swsRes] = await Promise.all([
        axios.get(`${API_URL}/delegation-tasks`, { headers: getAuthHeader() }),
        // include_all_master=true to show ALL allocated tasks including future ones
        axios.get(`${API_URL}/checklist-instances/my-tasks?include_all_master=true`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/sws-tasks/my-tasks?status=pending,in_progress,completed`, { headers: getAuthHeader() })
      ]);
      
      // Filter tasks assigned to current user and exclude cancelled tasks
      const myDelegation = delegationRes.data.filter(t => 
        (t.assigned_to_id === user?.id || t.assigned_to === user?.id) &&
        t.status !== 'cancelled'
      );
      // Checklist instances are already filtered by the backend endpoint
      // Also filter out schedule_invalidated instances (tasks whose schedule was changed)
      const myChecklist = checklistRes.data.filter(t => !t.schedule_invalidated);
      // SWS tasks are already filtered by the backend endpoint
      const mySws = swsRes.data;
      
      setDelegationTasks(myDelegation);
      setChecklistTasks(myChecklist);
      setSwsTasks(mySws);
    } catch (error) {
      console.error('Failed to fetch tasks');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) fetchAllTasks();
  }, [user]);

  // Focus-based refresh: re-fetch when tab becomes visible again
  useEffect(() => {
    if (!user) return;
    const handler = () => { if (document.visibilityState === 'visible') fetchAllTasks(); };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, [user]);

  const markDelegationDone = async (task) => {
    const taskId = typeof task === 'string' ? task : task?.id;
    const taskObj = typeof task === 'object' ? task : delegationTasks.find(t => t.id === taskId);
    if (taskObj && taskObj.completion_mode === 'system-action') {
      toast.error(taskObj.auto_complete_action
        ? `This task auto-completes when ${taskObj.auto_complete_action}. Manual completion is disabled.`
        : 'This task auto-completes via the system.');
      return;
    }
    try {
      await axios.patch(`${API_URL}/delegation-tasks/${taskId}`, { status: 'completed' }, { headers: getAuthHeader() });
      toast.success('Task marked as done!');
      fetchAllTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update task');
    }
  };

  // Open CA completion dialog and fetch complaint details
  const openCADialog = async (task) => {
    try {
      const complaintId = task.related_entity_id || task.source_id;
      const response = await axios.get(`${API_URL}/complaints/${complaintId}`, { headers: getAuthHeader() });
      setCaDialog({ open: true, task, complaint: response.data });
      setCaForm({
        completion_notes: '',
        cost_analysis: {
          material_losses: [],
          manpower_losses: [],
          other_costs_description: '',
          other_costs_amount: 0
        }
      });
    } catch (error) {
      toast.error('Failed to load complaint details');
    }
  };

  // Open PA completion dialog and fetch complaint details
  const openPADialog = async (task) => {
    try {
      const complaintId = task.related_entity_id || task.source_id;
      const response = await axios.get(`${API_URL}/complaints/${complaintId}`, { headers: getAuthHeader() });
      setPaDialog({ open: true, task, complaint: response.data });
      setPaForm({ 
        root_cause_analysis: response.data.pa_root_cause_analysis || '',
        preventive_action: response.data.pa_description || '',
        completion_notes: '' 
      });
    } catch (error) {
      toast.error('Failed to load complaint details');
    }
  };

  // Submit CA completion
  const submitCACompletion = async () => {
    if (!caForm.completion_notes) {
      toast.error('Please provide completion notes');
      return;
    }
    setIsSubmitting(true);
    try {
      const complaintId = caDialog.task.related_entity_id || caDialog.task.source_id;
      await axios.post(`${API_URL}/complaints/${complaintId}/complete-ca`, caForm, { headers: getAuthHeader() });
      toast.success('Corrective Action completed successfully!');
      setCaDialog({ open: false, task: null, complaint: null });
      fetchAllTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete CA');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Submit PA completion
  const submitPACompletion = async () => {
    if (!paForm.root_cause_analysis || !paForm.preventive_action) {
      toast.error('Please provide RCA and Preventive Action');
      return;
    }
    setIsSubmitting(true);
    try {
      const complaintId = paDialog.task.related_entity_id || paDialog.task.source_id;
      await axios.post(`${API_URL}/complaints/${complaintId}/complete-pa`, {
        root_cause_analysis: paForm.root_cause_analysis,
        preventive_action: paForm.preventive_action,
        completion_notes: paForm.completion_notes
      }, { headers: getAuthHeader() });
      toast.success('Preventive Action completed successfully!');
      setPaDialog({ open: false, task: null, complaint: null });
      fetchAllTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete PA');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Cost analysis helpers
  const addMaterialLoss = () => {
    setCaForm(prev => ({
      ...prev,
      cost_analysis: {
        ...prev.cost_analysis,
        material_losses: [...prev.cost_analysis.material_losses, { item_name: '', quantity: 0, unit_cost: 0, total_cost: 0 }]
      }
    }));
  };

  const updateMaterialLoss = (index, field, value) => {
    setCaForm(prev => {
      const updated = [...prev.cost_analysis.material_losses];
      updated[index] = { ...updated[index], [field]: value };
      if (field === 'quantity' || field === 'unit_cost') {
        updated[index].total_cost = updated[index].quantity * updated[index].unit_cost;
      }
      return { ...prev, cost_analysis: { ...prev.cost_analysis, material_losses: updated } };
    });
  };

  const removeMaterialLoss = (index) => {
    setCaForm(prev => ({
      ...prev,
      cost_analysis: { ...prev.cost_analysis, material_losses: prev.cost_analysis.material_losses.filter((_, i) => i !== index) }
    }));
  };

  const addManpowerLoss = () => {
    setCaForm(prev => ({
      ...prev,
      cost_analysis: {
        ...prev.cost_analysis,
        manpower_losses: [...prev.cost_analysis.manpower_losses, { worker_name: '', hours: 0, hourly_rate: 0, total_cost: 0 }]
      }
    }));
  };

  const updateManpowerLoss = (index, field, value) => {
    setCaForm(prev => {
      const updated = [...prev.cost_analysis.manpower_losses];
      updated[index] = { ...updated[index], [field]: value };
      if (field === 'hours' || field === 'hourly_rate') {
        updated[index].total_cost = updated[index].hours * updated[index].hourly_rate;
      }
      return { ...prev, cost_analysis: { ...prev.cost_analysis, manpower_losses: updated } };
    });
  };

  const removeManpowerLoss = (index) => {
    setCaForm(prev => ({
      ...prev,
      cost_analysis: { ...prev.cost_analysis, manpower_losses: prev.cost_analysis.manpower_losses.filter((_, i) => i !== index) }
    }));
  };

  const getTotalCostImpact = () => {
    const materialTotal = caForm.cost_analysis.material_losses.reduce((sum, m) => sum + (m.total_cost || 0), 0);
    const manpowerTotal = caForm.cost_analysis.manpower_losses.reduce((sum, m) => sum + (m.total_cost || 0), 0);
    const otherTotal = caForm.cost_analysis.other_costs_amount || 0;
    return materialTotal + manpowerTotal + otherTotal;
  };

  const markChecklistDone = async (taskId) => {
    try {
      await axios.patch(`${API_URL}/checklist-instances/${taskId}/complete`, {}, { headers: getAuthHeader() });
      toast.success('Checklist completed!');
      fetchAllTasks();
    } catch (error) {
      toast.error('Failed to update checklist');
    }
  };

  // Check if SWS task has a pre-defined action (should not show manual Done button)
  // Get the action type for SWS task
  const getSwsActionType = (task) => {
    if (!task) return null;
    if (task.related_entity_type === 'item_code_request') return 'item_code_request';
    const title = task.title?.toLowerCase() || '';
    if (title.includes('create bom')) return 'create_bom';
    if (title.includes('create wo')) return 'create_wo';
    return 'generic';
  };

  // Open SWS View dialog for tasks with pre-defined actions
  const openSwsViewDialog = (task) => {
    setSwsViewDialog({ open: true, task });
  };

  // Handle SWS task action button click
  const handleSwsAction = (task) => {
    const actionType = getSwsActionType(task);
    const triggerData = task.trigger_data || {};
    
    switch (actionType) {
      case 'item_code_request':
        navigate(`/item-code-requests-register?request_id=${task.related_entity_id}`);
        break;
      case 'create_bom':
        // Navigate to BOM page with create=true and item pre-selected
        const bomParams = new URLSearchParams({
          create: 'true',
          item_id: triggerData.item_id || '',
          item_name: triggerData.item_name || '',
          so_id: triggerData.sales_order_id || '',
          so_number: triggerData.so_number || ''
        });
        navigate(`/production/bom?${bomParams.toString()}`);
        break;
      case 'create_wo':
        // Navigate to main Work Orders dashboard - user can combine multiple SOs there
        navigate('/production/work-orders');
        toast.info('Create a Work Order from this page. You can combine multiple SOs for the same item.');
        break;
      default:
        // Generic - show completion dialog
        setSwsDialog({ open: true, task });
        setCompletionNotes('');
    }
    setSwsViewDialog({ open: false, task: null });
  };

  // Open SWS completion dialog or view dialog based on task type
  const openSwsDialog = (task) => {
    // Block auto-complete tasks from manual completion (canonical check via completion_mode)
    if (task?.completion_mode === 'system-action' || task?.auto_complete_event) {
      const msg = task.auto_complete_action
        ? `This task auto-completes when ${task.auto_complete_action}.`
        : 'This task will be automatically completed when the related system action is performed.';
      toast.info(msg);
      return;
    }
    
    const actionType = getSwsActionType(task);
    
    // For tasks with pre-defined actions, show view dialog first
    if (actionType !== 'generic') {
      openSwsViewDialog(task);
      return;
    }
    
    // For generic tasks, show completion dialog
    setSwsDialog({ open: true, task });
    setCompletionNotes('');
  };

  // Complete SWS task
  const handleSwsComplete = async () => {
    if (!swsDialog.task) return;
    
    setIsSubmitting(true);
    try {
      await axios.post(
        `${API_URL}/sws-tasks/${swsDialog.task.id}/complete`,
        { remarks: completionNotes || null },
        { headers: getAuthHeader() }
      );
      
      toast.success(`Task "${swsDialog.task.title}" marked as complete`);
      setSwsDialog({ open: false, task: null });
      fetchAllTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete task');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Undo task completion (only allowed for tasks completed today)
  const undoTaskCompletion = async (task, type) => {
    try {
      let endpoint;
      if (type === 'delegation') {
        endpoint = `${API_URL}/tasks/${task.id}/undo-complete?task_type=delegation`;
      } else if (type === 'checklist') {
        endpoint = `${API_URL}/checklist-instances/${task.id}/undo`;
      } else {
        throw new Error('Unknown task type');
      }
      
      await axios.patch(endpoint, {}, { headers: getAuthHeader() });
      toast.success('Task marked as pending');
      fetchAllTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to undo completion');
    }
  };

  // Check if task was completed today (for Undo eligibility)
  const isCompletedToday = (task) => {
    if (!task.completed_at) return false;
    const completedDate = parseISO(task.completed_at);
    return isToday(completedDate);
  };

  const getTaskStatus = (task, type) => {
    if (task.status === 'completed' || task.status === 'done') return 'done';
    if (task.status === 'discontinued') return 'discontinued';
    if (task.status === 'future' || task._is_master_task) return 'future';
    
    let deadline;
    if (type === 'delegation') {
      deadline = task.due_date ? parseISO(task.due_date) : (task.deadline ? parseISO(task.deadline) : null);
    } else if (type === 'checklist') {
      deadline = task.due_date ? parseISO(task.due_date) : (task.next_due ? parseISO(task.next_due) : null);
    }
    
    // Only mark as overdue if deadline is before TODAY at start of day (not just before now)
    // Tasks due today should NOT be marked as overdue
    if (deadline && isBefore(deadline, startOfDay(new Date()))) return 'overdue';
    return 'pending';
  };

  // Calculate due time for SWS step based on previous step completion + TAT
  const calculateStepDueTime = (task, stepIndex) => {
    const step = task.steps[stepIndex];
    let startTime;
    
    if (stepIndex === 0) {
      startTime = task.started_at ? parseISO(task.started_at) : new Date();
    } else {
      const prevStep = task.steps[stepIndex - 1];
      if (prevStep.completed_at) {
        startTime = parseISO(prevStep.completed_at);
      } else if (step.started_at) {
        startTime = parseISO(step.started_at);
      } else {
        startTime = new Date();
      }
    }
    
    const dueTime = addHours(startTime, step.tat_hours || 0);
    return dueTime;
  };

  // Get step urgency status
  const getStepUrgency = (dueTime) => {
    const now = new Date();
    const hoursRemaining = differenceInHours(dueTime, now);
    
    if (isBefore(dueTime, now)) {
      return { status: 'delayed', color: 'bg-red-100 text-red-700 border-red-300', icon: AlertCircle };
    } else if (hoursRemaining <= 2) {
      return { status: 'due_soon', color: 'bg-orange-100 text-orange-700 border-orange-300', icon: AlertTriangle };
    }
    return { status: 'on_track', color: 'bg-green-100 text-green-700 border-green-300', icon: Clock };
  };

  // Check if step has manual completion (no completion_trigger)
  const isManualCompletionStep = (step) => {
    return !step.completion_trigger || step.completion_trigger === '' || step.completion_trigger === 'none';
  };

  // Sort tasks by EFFECTIVE due date (ascending) FIRST, then by priority
  // Priority: rolled_over_due_date > revised_due_date > due_date
  const sortTasks = (tasks) => {
    return [...tasks].sort((a, b) => {
      // Get effective due date (priority: rolled_over > revised > due_date)
      const getEffectiveDate = (task) => {
        if (task.rolled_over_due_date) {
          return task.rolled_over_due_date;
        }
        if (task.revised_due_date) {
          return task.revised_due_date;
        }
        if (task._type === 'sws') {
          return task.due_at; // SWS uses due_at timestamp
        }
        return task._type === 'delegation' 
          ? (task.due_date || task.deadline) 
          : (task.due_date || task.next_due);
      };
      
      const dateA = getEffectiveDate(a);
      const dateB = getEffectiveDate(b);
      
      if (dateA && dateB) {
        const dateCompare = new Date(dateA) - new Date(dateB);
        if (dateCompare !== 0) return dateCompare;
      } else if (dateA && !dateB) {
        return -1; // Tasks with dates come before tasks without dates
      } else if (!dateA && dateB) {
        return 1;
      }
      
      // SECOND: Within the same date, sort by priority (IMP/critical first)
      const priorityOrder = { 'IMP': 0, 'critical': 0, 'high': 1, 'medium': 2, 'low': 3, 'normal': 3, 'regular': 3 };
      const priorityA = priorityOrder[(a.priority || 'normal')] ?? 4;
      const priorityB = priorityOrder[(b.priority || 'normal')] ?? 4;
      return priorityA - priorityB;
    });
  };

  // Get pending tasks by type (exclude both 'completed' and 'done' statuses)
  const pendingDelegation = delegationTasks.filter(t => t.status !== 'completed' && t.status !== 'done' && t.status !== 'discontinued').map(t => ({ ...t, _type: 'delegation' }));
  const pendingChecklist = checklistTasks.filter(t => t.status !== 'completed' && t.status !== 'done' && t.status !== 'discontinued').map(t => ({ ...t, _type: 'checklist' }));
  const pendingSws = swsTasks.filter(t => t.status !== 'completed' && t.status !== 'done' && t.status !== 'discontinued').map(t => ({ ...t, _type: 'sws' }));
  const allPendingTasks = sortTasks([...pendingDelegation, ...pendingChecklist, ...pendingSws]);

  // Get completed tasks (accept both 'completed' and 'done' statuses, sorted by completed_at descending)
  const completedDelegation = delegationTasks.filter(t => t.status === 'completed' || t.status === 'done').map(t => ({ ...t, _type: 'delegation' }));
  const completedChecklist = checklistTasks.filter(t => t.status === 'completed' || t.status === 'done').map(t => ({ ...t, _type: 'checklist' }));
  const completedSws = swsTasks.filter(t => t.status === 'completed' || t.status === 'done').map(t => ({ ...t, _type: 'sws' }));
  const allCompletedTasks = [...completedDelegation, ...completedChecklist, ...completedSws].sort((a, b) => {
    const dateA = a.completed_at ? new Date(a.completed_at) : new Date(0);
    const dateB = b.completed_at ? new Date(b.completed_at) : new Date(0);
    return dateB - dateA; // Most recent first
  });

  // Stats
  const stats = {
    total: pendingDelegation.length + pendingChecklist.length + pendingSws.length,
    delegation: pendingDelegation.length,
    checklist: pendingChecklist.length,
    sws: pendingSws.length,
    overdue: delegationTasks.filter(t => getTaskStatus(t, 'delegation') === 'overdue').length +
             checklistTasks.filter(t => getTaskStatus(t, 'checklist') === 'overdue').length +
             swsTasks.filter(t => getTaskStatus(t, 'sws') === 'overdue').length,
    dueToday: delegationTasks.filter(t => {
      const dueDate = t.due_date || t.deadline;
      return dueDate && isToday(parseISO(dueDate)) && t.status !== 'completed' && t.status !== 'done' && t.status !== 'discontinued';
    }).length +
    checklistTasks.filter(t => {
      const dueDate = t.due_date || t.next_due;
      return dueDate && isToday(parseISO(dueDate)) && t.status !== 'completed' && t.status !== 'done' && t.status !== 'discontinued';
    }).length +
    swsTasks.filter(t => {
      const dueTime = t.due_at;
      return dueTime && isToday(parseISO(dueTime)) && t.status !== 'completed' && t.status !== 'done' && t.status !== 'discontinued';
    }).length,
    completed: delegationTasks.filter(t => t.status === 'completed' || t.status === 'done').length +
               checklistTasks.filter(t => t.status === 'completed' || t.status === 'done').length +
               swsTasks.filter(t => t.status === 'completed' || t.status === 'done').length,
    critical: delegationTasks.filter(t => (t.priority === 'critical' || t.priority === 'IMP') && t.status !== 'completed' && t.status !== 'done' && t.status !== 'discontinued').length +
              checklistTasks.filter(t => (t.priority === 'critical' || t.priority === 'IMP') && t.status !== 'completed' && t.status !== 'done' && t.status !== 'discontinued').length
  };

  const TaskCard = ({ task, type, onMarkDone }) => {
    const status = getTaskStatus(task, type);
    const deadline = type === 'delegation' ? (task.due_date || task.deadline) : 
                     type === 'sws' ? task.due_at :
                     (task.due_date || task.next_due);
    const isCritical = task.priority === 'critical' || task.priority === 'IMP';
    
    // Date tracking for delegation tasks:
    // - original_due_date: First due date ever set
    // - revised_due_date: User's manual shift
    // - rolled_over_due_date: Latest rollover date
    const originalDueDate = task.original_due_date;
    const revisedDueDate = task.revised_due_date;
    const rolledDueDate = task.rolled_over_due_date;
    
    // Check various states
    const isRolledOver = !!rolledDueDate;
    const isRevised = !!revisedDueDate;
    const hasDateHistory = originalDueDate && (isRevised || isRolledOver);
    
    // Effective due date (what the task is actually due)
    const effectiveDueDate = rolledDueDate || revisedDueDate || deadline;
    
    // Canonical auto-vs-manual classification via backend-provided fields.
    // Falls back to legacy source_type sniffing for tasks older than the
    // backend enrichment deployment (safety net; no new path depends on it).
    const behavior = useTaskCompletionBehavior(task);
    const isAutoCompleteOnly = !behavior.canManuallyComplete;
    // Retained for message/routing of CA/PA-specific completion dialogs:
    const isCATask = task.source_type === 'complaint_ca';
    const isPATask = task.source_type === 'complaint_pa';
    const isPaymentTask = task.source_type === 'payment_request';
    const isComplaintLinked = isCATask || isPATask;
    
    // SWS task urgency
    const getSwsUrgency = () => {
      if (type !== 'sws' || !task.due_at) return null;
      const dueTime = parseISO(task.due_at);
      const now = new Date();
      const hoursRemaining = differenceInHours(dueTime, now);
      const minutesRemaining = differenceInMinutes(dueTime, now);
      
      if (isBefore(dueTime, now)) {
        return { color: 'bg-red-100 text-red-700 border-red-300', label: 'Overdue', icon: AlertCircle };
      } else if (hoursRemaining < 1) {
        return { color: 'bg-orange-100 text-orange-700 border-orange-300', label: `${minutesRemaining}m left`, icon: AlertTriangle };
      } else if (hoursRemaining < 2) {
        return { color: 'bg-yellow-100 text-yellow-700 border-yellow-300', label: `${hoursRemaining}h left`, icon: Clock };
      }
      return { color: 'bg-green-100 text-green-700 border-green-300', label: `${hoursRemaining}h left`, icon: Clock };
    };
    
    const swsUrgency = getSwsUrgency();
    
    // Render date display for delegation tasks with history
    const renderDelegationDates = () => {
      if (type !== 'delegation') return null;
      
      // Case 1: Has original, revised, and rolled over (full history)
      if (originalDueDate && isRevised && isRolledOver) {
        return (
          <div className="flex flex-col gap-0.5">
            <span className="text-xs text-muted-foreground">
              Original: <span className="line-through">{formatDate(originalDueDate)}</span>
            </span>
            <span className="text-xs text-muted-foreground">
              Shifted: <span className="line-through">{formatDate(revisedDueDate)}</span>
            </span>
            <span className="flex items-center gap-1 text-amber-700 font-medium">
              <Calendar className="w-4 h-4" />
              Due: {formatDate(rolledDueDate)} <span className="text-xs">(R/O)</span>
            </span>
          </div>
        );
      }
      
      // Case 2: Has original and rolled over (no manual revision)
      if (originalDueDate && isRolledOver && !isRevised) {
        return (
          <div className="flex flex-col gap-0.5">
            <span className="text-xs text-muted-foreground">
              Original: <span className="line-through">{formatDate(originalDueDate)}</span>
            </span>
            <span className="flex items-center gap-1 text-amber-700 font-medium">
              <Calendar className="w-4 h-4" />
              Due: {formatDate(rolledDueDate)} <span className="text-xs">(R/O)</span>
            </span>
          </div>
        );
      }
      
      // Case 3: Has original and revised (no current rollover)
      if (originalDueDate && isRevised && !isRolledOver) {
        return (
          <div className="flex flex-col gap-0.5">
            <span className="text-xs text-muted-foreground">
              Original: <span className="line-through">{formatDate(originalDueDate)}</span>
            </span>
            <span className="flex items-center gap-1 text-blue-700 font-medium">
              <Calendar className="w-4 h-4" />
              Due: {formatDate(revisedDueDate)} <span className="text-xs">(Shifted)</span>
            </span>
          </div>
        );
      }
      
      // Case 4: Simple case - just due date
      return null;
    };
    
    return (
      <Card className={`${isCritical ? 'border-red-400 border-2' : ''} ${isAutoCompleteOnly ? 'border-l-4 border-l-orange-500' : ''} ${isRolledOver ? 'border-l-4 border-l-amber-500' : ''} ${type === 'sws' && swsUrgency ? 'border-l-4 border-l-blue-500' : ''}`} data-testid={`task-card-${task.id}`}>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {type === 'delegation' ? (
                <ListTodo className="w-4 h-4 text-blue-500" />
              ) : type === 'sws' ? (
                <Workflow className="w-4 h-4 text-orange-500" />
              ) : (
                <CheckSquare className="w-4 h-4 text-green-500" />
              )}
              <CardTitle className="text-base">{task.title || task.name}</CardTitle>
              {isCritical && (
                <Badge className="bg-red-600 text-white" data-testid="critical-badge">
                  <Zap className="w-3 h-3 mr-1" />
                  IMP
                </Badge>
              )}
              {isRolledOver && (
                <Badge className="bg-amber-100 text-amber-800 border-amber-300" variant="outline" data-testid="rolled-over-badge">
                  <RotateCcw className="w-3 h-3 mr-1" />
                  R/O
                </Badge>
              )}
              {isRevised && !isRolledOver && (
                <Badge className="bg-blue-100 text-blue-800 border-blue-300" variant="outline" data-testid="shifted-badge">
                  Shifted
                </Badge>
              )}
              {type === 'sws' && swsUrgency && (
                <Badge variant="outline" className={swsUrgency.color}>
                  {React.createElement(swsUrgency.icon, { className: "w-3 h-3 mr-1" })}
                  {swsUrgency.label}
                </Badge>
              )}
              {/* Coverage Mode: Show badge if covering for someone on leave */}
              {type === 'sws' && task.is_backup_coverage && task.original_assignee_name && (
                <Badge variant="outline" className="bg-blue-100 text-blue-700 border-blue-300" data-testid="covering-badge">
                  🔄 Covering for {task.original_assignee_name}
                </Badge>
              )}
            </div>
            <Badge variant="outline" className={statusColors[status]}>
              {status === 'done' ? 'Done' : status === 'overdue' ? 'Overdue' : status === 'future' ? 'Future' : 'Pending'}
            </Badge>
          </div>
          {task.description && (
            <CardDescription className="mt-1">{task.description}</CardDescription>
          )}
          {type === 'sws' && task.workflow_name && (
            <p className="text-xs text-muted-foreground mt-1">Workflow: {task.workflow_name}</p>
          )}
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              {/* Date display for delegation tasks with history */}
              {type === 'delegation' && hasDateHistory ? (
                renderDelegationDates()
              ) : type === 'sws' && deadline ? (
                <span className={`flex items-center gap-1 ${status === 'overdue' ? 'text-red-600 font-medium' : ''}`}>
                  <Calendar className="w-4 h-4" />
                  {new Date(deadline).toLocaleString('en-GB', { 
                    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' 
                  })}
                </span>
              ) : deadline && (
                <span className={`flex items-center gap-1 ${status === 'overdue' ? 'text-red-600 font-medium' : ''}`}>
                  <Calendar className="w-4 h-4" />
                  {formatDate(deadline)}
                </span>
              )}
              {/* Show task type indicator */}
              <Badge variant="outline" className="text-xs">
                {type === 'delegation' ? 'Delegation' : type === 'sws' ? 'SWS' : 'Checklist'}
              </Badge>
              {/* Show frequency for checklist tasks */}
              {type === 'checklist' && task.frequency && (
                <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-300 capitalize">
                  {task.frequency}
                </Badge>
              )}
              {/* Show TAT for SWS tasks */}
              {type === 'sws' && task.tat_value && (
                <Badge variant="outline" className="text-xs bg-orange-50 text-orange-700 border-orange-300">
                  TAT: {task.tat_value} {task.tat_unit}
                </Badge>
              )}
              {/* Show future indicator */}
              {(task._category === 'future_master' || task._is_master_task || status === 'future') && (
                <Badge variant="outline" className="text-xs bg-indigo-50 text-indigo-700 border-indigo-300">
                  Future Task
                </Badge>
              )}
              {/* Show upcoming indicator */}
              {task._category === 'upcoming' && !task._is_master_task && (
                <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-300">
                  Upcoming
                </Badge>
              )}
              {isComplaintLinked && (
                <Badge variant="outline" className="text-xs bg-orange-50 text-orange-700 border-orange-300">
                  {isCATask ? 'CA' : 'PA'} Task
                </Badge>
              )}
              {isPaymentTask && (
                <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-300">
                  Payment Task
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-2">
              {/* Two-state rendering: navigation button if navigationTarget present, else Done.
                  Backend label drives the text — "Take Action" for system-action, workflow-specific
                  for manual-with-nav (Create BOM, Item Code Request). */}
              {status !== 'done' && status !== 'future' && behavior.navigationTarget && (
                <Button
                  size="sm"
                  variant="outline"
                  className="border-blue-600 text-blue-600 hover:bg-blue-50"
                  onClick={() => navigate(behavior.navigationTarget.path)}
                  data-testid={`nav-task-${task.id}`}
                >
                  {behavior.navigationTarget.label}
                </Button>
              )}
              {status !== 'done' && status !== 'future' && !behavior.navigationTarget && (
                <Button size="sm" onClick={onMarkDone} className="bg-green-600 hover:bg-green-700" data-testid={`mark-done-${task.id}`}>
                  <CheckCircle2 className="w-4 h-4 mr-1" />
                  Done
                </Button>
              )}
            </div>
          </div>
          {isCritical && (
            <p className="text-xs text-red-600 mt-2 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" />
              IMP task - Missing this attracts double penalty
            </p>
          )}
          {/* Show date history info for rolled/shifted tasks */}
          {hasDateHistory && !isCritical && (
            <div className="text-xs mt-2 space-y-1">
              {isRevised && isRolledOver && (
                <p className="text-amber-600 flex items-center gap-1">
                  <RotateCcw className="w-3 h-3" />
                  Originally {formatDate(originalDueDate)} → Shifted to {formatDate(revisedDueDate)} → Rolled over to {formatDate(rolledDueDate)}
                </p>
              )}
              {isRolledOver && !isRevised && (
                <p className="text-amber-600 flex items-center gap-1">
                  <RotateCcw className="w-3 h-3" />
                  Task rolled over from {formatDate(originalDueDate)} to {formatDate(rolledDueDate)}
                </p>
              )}
              {isRevised && !isRolledOver && (
                <p className="text-blue-600 flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  Due date shifted from {formatDate(originalDueDate)} to {formatDate(revisedDueDate)}
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  // Empty state component
  const EmptyState = ({ message }) => (
    <Card>
      <CardContent className="py-12 text-center">
        <CheckCircle2 className="w-12 h-12 mx-auto text-green-500 mb-4" />
        <p className="text-muted-foreground">{message}</p>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="my-tasks-page">
        <div>
          <h1 className="text-3xl font-bold">My Tasks</h1>
          <p className="text-muted-foreground">All tasks assigned to you</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold" data-testid="stat-total">{stats.total}</div>
              <p className="text-sm text-muted-foreground">Total Pending</p>
            </CardContent>
          </Card>
          <Card className="border-red-200">
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-red-600" data-testid="stat-critical">{stats.critical}</div>
              <p className="text-sm text-muted-foreground flex items-center gap-1">
                <Zap className="w-3 h-3" />
                Critical
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-red-600" data-testid="stat-overdue">{stats.overdue}</div>
              <p className="text-sm text-muted-foreground">Overdue</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-orange-600" data-testid="stat-today">{stats.dueToday}</div>
              <p className="text-sm text-muted-foreground">Due Today</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-green-600" data-testid="stat-completed">{stats.completed}</div>
              <p className="text-sm text-muted-foreground">Completed</p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs for task types */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full" data-testid="task-tabs">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="all" data-testid="tab-all">
              All Tasks ({stats.total})
            </TabsTrigger>
            <TabsTrigger value="delegation" data-testid="tab-delegation">
              Delegation ({stats.delegation})
            </TabsTrigger>
            <TabsTrigger value="checklist" data-testid="tab-checklist">
              Checklist ({stats.checklist})
            </TabsTrigger>
            <TabsTrigger value="sws" data-testid="tab-sws">
              SWS ({stats.sws})
            </TabsTrigger>
            <TabsTrigger value="completed" data-testid="tab-completed">
              Completed ({stats.completed})
            </TabsTrigger>
          </TabsList>

          {/* All Tasks Tab */}
          <TabsContent value="all" className="space-y-4" data-testid="content-all">
            {allPendingTasks.length === 0 ? (
              <EmptyState message="All caught up! No pending tasks." />
            ) : (
              sortTasks(allPendingTasks).map((task) => (
                <TaskCard
                  key={task.id}
                  task={task}
                  type={task._type}
                  onMarkDone={() => {
                    if (task._type === 'delegation') markDelegationDone(task);
                    else if (task._type === 'checklist') markChecklistDone(task.id);
                    else if (task._type === 'sws') openSwsDialog(task);
                  }}
                />
              ))
            )}
          </TabsContent>

          {/* Delegation Tab */}
          <TabsContent value="delegation" className="space-y-4" data-testid="content-delegation">
            {pendingDelegation.length === 0 ? (
              <EmptyState message="No pending delegation tasks." />
            ) : (
              sortTasks(pendingDelegation).map((task) => (
                <TaskCard
                  key={task.id}
                  task={task}
                  type="delegation"
                  onMarkDone={() => markDelegationDone(task)}
                />
              ))
            )}
          </TabsContent>

          {/* Checklist Tab */}
          <TabsContent value="checklist" className="space-y-4" data-testid="content-checklist">
            {pendingChecklist.length === 0 ? (
              <EmptyState message="No pending checklist tasks." />
            ) : (
              sortTasks(pendingChecklist).map((task) => (
                <TaskCard
                  key={task.id}
                  task={task}
                  type="checklist"
                  onMarkDone={() => markChecklistDone(task.id)}
                />
              ))
            )}
          </TabsContent>

          {/* SWS Tab */}
          <TabsContent value="sws" className="space-y-4" data-testid="content-sws">
            {swsTasks.length === 0 ? (
              <EmptyState message="No pending SWS workflow tasks." />
            ) : (
              swsTasks.map((task) => (
                <TaskCard 
                  key={task.id} 
                  task={task} 
                  type="sws"
                  onMarkDone={() => openSwsDialog(task)}
                />
              ))
            )}
          </TabsContent>

          {/* Completed Tasks Tab */}
          <TabsContent value="completed" className="space-y-4" data-testid="content-completed">
            {allCompletedTasks.length === 0 ? (
              <EmptyState message="No completed tasks yet." />
            ) : (
              <>
                <p className="text-sm text-muted-foreground mb-4">
                  Showing {allCompletedTasks.length} completed tasks. Tasks completed today can be undone.
                </p>
                {allCompletedTasks.map((task) => (
                  <Card key={task.id} className="border-green-200 bg-green-50/30" data-testid={`completed-task-${task.id}`}>
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4 text-green-600" />
                          <CardTitle className="text-base line-through text-muted-foreground">
                            {task.title || task.name}
                          </CardTitle>
                          {(task.priority === 'IMP' || task.priority === 'critical') && (
                            <Badge className="bg-red-100 text-red-800" variant="outline">IMP</Badge>
                          )}
                        </div>
                        <Badge variant="outline" className="bg-green-100 text-green-800 border-green-300">
                          Completed
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            Due: {formatDate(task._type === 'delegation' ? (task.due_date || task.deadline) : (task.due_date || task.next_due))}
                          </span>
                          <span className="flex items-center gap-1 text-green-600">
                            <CheckCircle2 className="w-4 h-4" />
                            Completed: {task.completed_at ? formatDate(task.completed_at) : 'N/A'}
                          </span>
                          <Badge variant="outline" className="text-xs">
                            {task._type === 'delegation' ? 'Delegation' : 'Checklist'}
                          </Badge>
                        </div>
                        {isCompletedToday(task) && !task.completion_note?.startsWith('Auto-completed') && (
                          <Button 
                            size="sm" 
                            variant="outline" 
                            onClick={() => undoTaskCompletion(task, task._type)}
                            className="text-orange-600 border-orange-300 hover:bg-orange-50"
                            data-testid={`undo-${task.id}`}
                          >
                            <RotateCcw className="w-4 h-4 mr-1" />
                            Undo
                          </Button>
                        )}
                        {task.completion_note?.startsWith('Auto-completed') && (
                          <span className="text-xs text-muted-foreground italic">Auto-completed</span>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* SWS Task Completion Dialog */}
      <Dialog open={swsDialog.open} onOpenChange={(open) => !open && setSwsDialog({ open: false, task: null })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Complete SWS Task</DialogTitle>
            <DialogDescription>
              {swsDialog.task?.title}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Completion Notes */}
            <div className="space-y-2">
              <Label>Notes (Optional)</Label>
              <Textarea
                placeholder="Add any notes about task completion..."
                value={completionNotes}
                onChange={(e) => setCompletionNotes(e.target.value)}
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setSwsDialog({ open: false, task: null })}>
              Cancel
            </Button>
            <Button 
              onClick={handleSwsComplete} 
              disabled={isSubmitting}
              className="bg-green-600 hover:bg-green-700"
            >
              {isSubmitting ? 'Completing...' : 'Mark Complete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* SWS View Dialog - for tasks with pre-defined actions */}
      <Dialog open={swsViewDialog.open} onOpenChange={(open) => !open && setSwsViewDialog({ open: false, task: null })}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-blue-600" />
              Task Details
            </DialogTitle>
            <DialogDescription>
              {swsViewDialog.task?.title}
            </DialogDescription>
          </DialogHeader>
          
          {swsViewDialog.task && (
            <div className="space-y-4 py-4">
              {/* Item Details */}
              <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                  <Package className="w-4 h-4" />
                  Item Information
                </div>
                
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-gray-500">Item Name:</span>
                    <p className="font-medium">{swsViewDialog.task.trigger_data?.item_name || '-'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Item Code:</span>
                    <p className="font-medium">{swsViewDialog.task.trigger_data?.item_code || '-'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Quantity:</span>
                    <p className="font-medium">{swsViewDialog.task.trigger_data?.quantity || '-'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">SO Number:</span>
                    <p className="font-medium">{swsViewDialog.task.trigger_data?.so_number || '-'}</p>
                  </div>
                </div>
                
                {/* Task Description - shows item details */}
                {swsViewDialog.task.description && (
                  <div className="mt-3 pt-3 border-t">
                    <span className="text-gray-500 text-sm">Details:</span>
                    <p className="mt-1 text-sm whitespace-pre-wrap">{swsViewDialog.task.description}</p>
                  </div>
                )}
                
                {/* Item Specs if available */}
                {swsViewDialog.task.trigger_data?.item_specs && (
                  <div className="mt-3 pt-3 border-t">
                    <span className="text-gray-500 text-sm">Specifications:</span>
                    <div className="mt-2">
                      {typeof swsViewDialog.task.trigger_data.item_specs === 'object' && !Array.isArray(swsViewDialog.task.trigger_data.item_specs) ? (
                        // If it's an object, iterate over entries
                        <div className="space-y-1">
                          {Object.entries(swsViewDialog.task.trigger_data.item_specs).map(([key, value]) => (
                            <p key={key} className="text-sm">
                              <span className="text-gray-600 capitalize">{key.replace(/_/g, ' ')}:</span>{' '}
                              <span className="font-medium">{value}</span>
                            </p>
                          ))}
                        </div>
                      ) : (
                        // If it's a string, display as-is with whitespace preserved
                        <p className="text-sm whitespace-pre-wrap">{swsViewDialog.task.trigger_data.item_specs}</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Workflow Info */}
              <div className="text-sm text-gray-500">
                <span>Workflow: </span>
                <span className="font-medium text-gray-700">{swsViewDialog.task.workflow_name}</span>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setSwsViewDialog({ open: false, task: null })}>
              Close
            </Button>
            {swsViewDialog.task && getSwsActionType(swsViewDialog.task) === 'create_bom' && (
              <Button 
                onClick={() => handleSwsAction(swsViewDialog.task)}
                className="bg-blue-600 hover:bg-blue-700"
              >
                Create BOM
              </Button>
            )}
            {swsViewDialog.task && getSwsActionType(swsViewDialog.task) === 'create_wo' && (
              <Button 
                onClick={() => handleSwsAction(swsViewDialog.task)}
                className="bg-blue-600 hover:bg-blue-700"
              >
                Create Work Order
              </Button>
            )}
            {swsViewDialog.task && getSwsActionType(swsViewDialog.task) === 'item_code_request' && (
              <Button 
                onClick={() => handleSwsAction(swsViewDialog.task)}
                className="bg-blue-600 hover:bg-blue-700"
              >
                Create Item Code
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CA Completion Dialog */}
      <Dialog open={caDialog.open} onOpenChange={(open) => !open && setCaDialog({ open: false, task: null, complaint: null })}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-yellow-600" />
              Complete Corrective Action
            </DialogTitle>
            <DialogDescription>
              {caDialog.complaint?.complaint_no}: {caDialog.complaint?.title}
            </DialogDescription>
          </DialogHeader>
          
          {caDialog.complaint && (
            <div className="space-y-4">
              {/* Complaint Summary */}
              <div className="bg-gray-50 p-4 rounded-lg border">
                <p className="text-sm"><strong>Description:</strong> {caDialog.complaint.description}</p>
                <p className="text-sm mt-2"><strong>Assigned Action:</strong> {caDialog.complaint.ca_description}</p>
              </div>

              {/* Images if present */}
              {caDialog.complaint.images && caDialog.complaint.images.length > 0 && (
                <div className="bg-blue-50/30 p-3 rounded-lg border">
                  <p className="text-sm font-medium mb-2">Attached Images</p>
                  <div className="flex gap-2 flex-wrap">
                    {caDialog.complaint.images.map((img, idx) => (
                      <a key={idx} href={img.startsWith('/api') ? `${API_URL.replace('/api', '')}${img}` : img} target="_blank" rel="noopener noreferrer">
                        <img src={img.startsWith('/api') ? `${API_URL.replace('/api', '')}${img}` : img} alt={`Image ${idx + 1}`} className="w-16 h-16 object-cover rounded border hover:opacity-80" />
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Completion Notes */}
              <div>
                <Label>Completion Notes *</Label>
                <Textarea
                  value={caForm.completion_notes}
                  onChange={(e) => setCaForm(prev => ({ ...prev, completion_notes: e.target.value }))}
                  placeholder="Describe what was done to resolve the issue..."
                  rows={3}
                  className="uppercase"
                />
              </div>

              {/* Cost Analysis */}
              <div className="border rounded-lg p-4 bg-red-50/30">
                <p className="font-semibold text-red-800 mb-3">Cost Analysis (Optional)</p>
                
                {/* Material Losses */}
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-sm">Material Losses</Label>
                    <Button size="sm" variant="outline" onClick={addMaterialLoss}>+ Add</Button>
                  </div>
                  {caForm.cost_analysis.material_losses.map((m, i) => (
                    <div key={i} className="grid grid-cols-5 gap-2 mb-2 items-end">
                      <input className="border rounded px-2 py-1 text-sm" placeholder="Item" value={m.item_name} onChange={(e) => updateMaterialLoss(i, 'item_name', e.target.value)} />
                      <input className="border rounded px-2 py-1 text-sm" type="text" inputMode="decimal" placeholder="Qty" value={m.quantity} onChange={(e) => {
                        const value = e.target.value;
                        if (value === '' || value === '-' || /^-?\d*\.?\d*$/.test(value)) {
                          updateMaterialLoss(i, 'quantity', parseFloat(value) || 0);
                        }
                      }} />
                      <input className="border rounded px-2 py-1 text-sm" type="text" inputMode="decimal" placeholder="Unit Cost" value={m.unit_cost} onChange={(e) => {
                        const value = e.target.value;
                        if (value === '' || value === '-' || /^-?\d*\.?\d*$/.test(value)) {
                          updateMaterialLoss(i, 'unit_cost', parseFloat(value) || 0);
                        }
                      }} />
                      <span className="text-sm font-medium">{formatCurrency(m.total_cost)}</span>
                      <Button size="sm" variant="ghost" className="text-red-600" onClick={() => removeMaterialLoss(i)}>×</Button>
                    </div>
                  ))}
                </div>

                {/* Manpower Losses */}
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-sm">Manpower Losses</Label>
                    <Button size="sm" variant="outline" onClick={addManpowerLoss}>+ Add</Button>
                  </div>
                  {caForm.cost_analysis.manpower_losses.map((m, i) => (
                    <div key={i} className="grid grid-cols-5 gap-2 mb-2 items-end">
                      <input className="border rounded px-2 py-1 text-sm" placeholder="Worker" value={m.worker_name} onChange={(e) => updateManpowerLoss(i, 'worker_name', e.target.value)} />
                      <input className="border rounded px-2 py-1 text-sm" type="text" inputMode="decimal" placeholder="Hours" value={m.hours} onChange={(e) => {
                        const value = e.target.value;
                        if (value === '' || value === '-' || /^-?\d*\.?\d*$/.test(value)) {
                          updateManpowerLoss(i, 'hours', parseFloat(value) || 0);
                        }
                      }} />
                      <input className="border rounded px-2 py-1 text-sm" type="text" inputMode="decimal" placeholder="Rate/Hr" value={m.hourly_rate} onChange={(e) => {
                        const value = e.target.value;
                        if (value === '' || value === '-' || /^-?\d*\.?\d*$/.test(value)) {
                          updateManpowerLoss(i, 'hourly_rate', parseFloat(value) || 0);
                        }
                      }} />
                      <span className="text-sm font-medium">{formatCurrency(m.total_cost)}</span>
                      <Button size="sm" variant="ghost" className="text-red-600" onClick={() => removeManpowerLoss(i)}>×</Button>
                    </div>
                  ))}
                </div>

                {/* Other Costs */}
                <div className="grid grid-cols-2 gap-4 mb-3">
                  <div>
                    <Label className="text-sm">Other Costs Description</Label>
                    <input className="w-full border rounded px-2 py-1 text-sm" placeholder="Machine downtime, etc." value={caForm.cost_analysis.other_costs_description} onChange={(e) => setCaForm(prev => ({ ...prev, cost_analysis: { ...prev.cost_analysis, other_costs_description: e.target.value }}))} />
                  </div>
                  <div>
                    <Label className="text-sm">Amount</Label>
                    <input className="w-full border rounded px-2 py-1 text-sm" type="text" inputMode="decimal" value={caForm.cost_analysis.other_costs_amount} onChange={(e) => {
                      const value = e.target.value;
                      if (value === '' || value === '-' || /^-?\d*\.?\d*$/.test(value)) {
                        setCaForm(prev => ({ ...prev, cost_analysis: { ...prev.cost_analysis, other_costs_amount: parseFloat(value) || 0 }}));
                      }
                    }} />
                  </div>
                </div>

                {/* Total */}
                <div className="bg-red-100 p-3 rounded text-center">
                  <p className="text-sm text-red-700">Total Financial Impact</p>
                  <p className="text-xl font-bold text-red-800">{formatCurrency(getTotalCostImpact())}</p>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCaDialog({ open: false, task: null, complaint: null })}>Cancel</Button>
            <Button className="bg-yellow-600 hover:bg-yellow-700" onClick={submitCACompletion} disabled={isSubmitting}>
              {isSubmitting ? 'Submitting...' : 'Complete CA'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PA Completion Dialog */}
      <Dialog open={paDialog.open} onOpenChange={(open) => !open && setPaDialog({ open: false, task: null, complaint: null })}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-indigo-600" />
              Complete Preventive Action
            </DialogTitle>
            <DialogDescription>
              {paDialog.complaint?.complaint_no}: {paDialog.complaint?.title}
            </DialogDescription>
          </DialogHeader>
          
          {paDialog.complaint && (
            <div className="space-y-4">
              {/* Complaint & CA Summary */}
              <div className="bg-gray-50 p-4 rounded-lg border">
                <p className="text-sm"><strong>Complaint:</strong> {paDialog.complaint.description}</p>
                {paDialog.complaint.ca_completion_notes && (
                  <p className="text-sm mt-2 p-2 bg-yellow-50 rounded border-l-4 border-yellow-400">
                    <strong>CA Completed:</strong> {paDialog.complaint.ca_completion_notes}
                  </p>
                )}
              </div>

              {/* Images if present */}
              {paDialog.complaint.images && paDialog.complaint.images.length > 0 && (
                <div className="bg-blue-50/30 p-3 rounded-lg border">
                  <p className="text-sm font-medium mb-2">Attached Images</p>
                  <div className="flex gap-2 flex-wrap">
                    {paDialog.complaint.images.map((img, idx) => (
                      <a key={idx} href={img.startsWith('/api') ? `${API_URL.replace('/api', '')}${img}` : img} target="_blank" rel="noopener noreferrer">
                        <img src={img.startsWith('/api') ? `${API_URL.replace('/api', '')}${img}` : img} alt={`Image ${idx + 1}`} className="w-16 h-16 object-cover rounded border hover:opacity-80" />
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Root Cause Analysis */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <Label className="text-amber-800 font-medium">Root Cause Analysis (RCA) *</Label>
                <p className="text-xs text-amber-600 mb-2">Why did this issue occur? Identify the underlying cause.</p>
                <Textarea
                  value={paForm.root_cause_analysis}
                  onChange={(e) => setPaForm(prev => ({ ...prev, root_cause_analysis: e.target.value }))}
                  placeholder="Example: Process deviation due to unclear work instructions..."
                  rows={3}
                  className="bg-white uppercase"
                />
              </div>

              {/* Preventive Action */}
              <div>
                <Label>Preventive Action Taken *</Label>
                <p className="text-xs text-muted-foreground mb-1">What steps were taken to prevent recurrence?</p>
                <Textarea
                  value={paForm.preventive_action}
                  onChange={(e) => setPaForm(prev => ({ ...prev, preventive_action: e.target.value }))}
                  placeholder="Example: Updated SOP document, conducted re-training..."
                  rows={3}
                  className="uppercase"
                />
              </div>

              {/* Completion Notes */}
              <div>
                <Label>Additional Notes (Optional)</Label>
                <Textarea
                  value={paForm.completion_notes}
                  onChange={(e) => setPaForm(prev => ({ ...prev, completion_notes: e.target.value }))}
                  placeholder="Any additional details or follow-up actions..."
                  rows={2}
                  className="uppercase"
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setPaDialog({ open: false, task: null, complaint: null })}>Cancel</Button>
            <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={submitPACompletion} disabled={isSubmitting}>
              {isSubmitting ? 'Submitting...' : 'Complete PA'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
